import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  PatientEmailHistoryDetails,
  PatientEmailTypeBadge,
} from '@/components/email/PatientEmailHistoryBits';
import { EmailDeliveryPill } from '@/components/patient/PatientEmailThreads';
import type { PatientEmailHistoryItem } from '@/hooks/usePatientEmailHistory';
import { formatCopenhagenEmailStamp } from '@/lib/emailHistory';
import { TABLE_HEADER } from '@/components/ui/pageChrome';
import { TYPE, UI_SECONDARY } from '@/lib/typography';
import { cn } from '@/utils/cn';

function Th({ children }: { children: string }) {
  return (
    <th scope="col" className={cn('px-3 py-3 text-left uppercase tracking-wide', TABLE_HEADER)}>
      {children}
    </th>
  );
}

export function InvoiceEmailHistoryList({
  rows,
  initiallyOpenId = null,
}: {
  rows: PatientEmailHistoryItem[];
  initiallyOpenId?: string | null;
}) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(initiallyOpenId);

  return (
    <table className={cn('mt-2 min-w-full', TYPE.body)}>
      <thead>
        <tr className="border-b border-ds-line">
          <Th>{t('invoices.field_issue_date')}</Th>
          <Th>{t('invoices.reminder_type_label')}</Th>
          <Th>{t('invoices.email_subject_label')}</Th>
          <Th>{t('invoices.email_recipient_label')}</Th>
          <Th>{t('invoices.col_status')}</Th>
          <th className="py-2" />
        </tr>
      </thead>
      <tbody className="divide-y divide-ds-line">
        {rows.map((row) => {
          const open = openId === row.id;
          const stamp = formatCopenhagenEmailStamp(row.created_at);
          return (
            <Fragment key={row.id}>
              <tr>
                <td className="whitespace-nowrap px-3 py-3 text-ds-muted">{stamp}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <PatientEmailTypeBadge emailType={row.email_type} />
                    {row.invoice_number ? (
                      <span className={cn(UI_SECONDARY, 'text-ds-muted')}>
                        #{row.invoice_number}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3 text-ds-text">{row.subject || t('email.no_subject')}</td>
                <td className="px-3 py-3 text-ds-text">{row.sent_to}</td>
                <td className="px-3 py-3">
                  <EmailDeliveryPill status={row.status} />
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : row.id)}
                    className="inline-flex items-center gap-1 text-[11px] text-ds-teal hover:text-ds-teal-700"
                  >
                    {open ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                    {open
                      ? t('patients.communication.hide_email_body')
                      : t('patients.communication.show_email_body')}
                  </button>
                </td>
              </tr>
              {open ? (
                <tr className="border-none">
                  <td colSpan={6} className="px-3 pb-3">
                    <PatientEmailHistoryDetails email={row} timestamp={stamp} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
